import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Container, Button, Fade, Form } from "react-bootstrap";

const GDPR_VERSION = "1.0.0";
const STORAGE_KEY = "gdprConsent";

const GDPRPopup = () => {
  const [visible, setVisible] = useState(false);
  const [render, setRender] = useState(false);
  const [mode, setMode] = useState("main"); // main | preferences

  // ✅ IMPORTANT: default optional OFF (no pre-ticked opt-in)
  const [optionalCookies, setOptionalCookies] = useState(false);

  // Helper: push consent state to GTM
  const pushConsent = (optional) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "consent_update",
      analytics_consent: optional ? "granted" : "denied",
      ad_consent: optional ? "granted" : "denied",
    });
  };

  const persistConsent = (optional) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: GDPR_VERSION,
        necessary: true,
        optional,
        timestamp: new Date().toISOString(),
      })
    );
  };

  const openBanner = () => {
    setRender(true);
    setTimeout(() => setVisible(true), 50);
  };

  const closeBanner = () => {
    setVisible(false);
    setTimeout(() => {
      setRender(false);
      setMode("main");
    }, 300);
  };

  const acceptAll = () => {
    persistConsent(true);
    pushConsent(true);
    closeBanner();
  };

  const rejectAll = () => {
    persistConsent(false);
    pushConsent(false);
    closeBanner();
  };

  const savePreferences = () => {
    persistConsent(!!optionalCookies);
    pushConsent(!!optionalCookies);
    closeBanner();
  };

  // On load: show banner if missing/old consent, else push stored state
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      openBanner();
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      openBanner();
      return;
    }

    if (parsed.version !== GDPR_VERSION) {
      openBanner();
      return;
    }

    const optional = !!parsed.optional;
    setOptionalCookies(optional);

    // ✅ Always push granted OR denied on startup for returning visitors
    pushConsent(optional);
  }, []);

  // ESC = reject (do not close silently)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") rejectAll();
    };

    if (visible) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!render) return null;

  return createPortal(
    <Fade in={visible} appear unmountOnExit>
      <div
        // ✅ Backdrop click = reject (no silent dismiss)
        onClick={rejectAll}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1054,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            bottom: "1rem",
            left: "1rem",
            right: "1rem",
            backgroundColor: "#00324c",
            color: "#fff",
            borderRadius: "10px",
            padding: "2rem 1.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            zIndex: 1055,
          }}
          role="dialog"
          aria-modal="true"
          aria-live="polite"
        >
          <Container className="p-0">
            {mode === "main" && (
              <>
                <h5 style={{ fontWeight: "bold", marginBottom: "1rem" }}>
                  Your privacy matters
                </h5>

                <p className="mb-3">
                  We use necessary cookies for essential website functions. With
                  your consent, we also use optional cookies for analytics,
                  marketing and conversion tracking.
                </p>

                <p className="mb-4">
                  You can change your choice anytime in our privacy settings.
                </p>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    onClick={acceptAll}
                    variant="light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Accept all cookies
                  </Button>

                  <Button
                    onClick={rejectAll}
                    variant="outline-light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Reject optional cookies
                  </Button>

                  <Button
                    onClick={() => setMode("preferences")}
                    variant="outline-light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Manage preferences
                  </Button>
                </div>
              </>
            )}

            {mode === "preferences" && (
              <>
                <h5 style={{ fontWeight: "bold", marginBottom: "1rem" }}>
                  Manage your cookie preferences
                </h5>

                <p className="mb-3">
                  Necessary cookies are required and cannot be disabled. Optional
                  cookies are only used with your consent.
                </p>

                <Form>
                  <Form.Check
                    type="switch"
                    label="Necessary cookies (required)"
                    checked
                    disabled
                    className="mb-3"
                  />

                  <Form.Check
                    type="switch"
                    label="Optional cookies (analytics / marketing / conversion tracking)"
                    checked={optionalCookies}
                    onChange={(e) => setOptionalCookies(e.target.checked)}
                    className="mb-4"
                  />
                </Form>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    onClick={savePreferences}
                    variant="light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Save preferences
                  </Button>

                  <Button
                    onClick={rejectAll}
                    variant="outline-light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Reject optional cookies
                  </Button>

                  <Button
                    onClick={() => setMode("main")}
                    variant="outline-light"
                    style={{ borderRadius: "30px", fontWeight: 600, minWidth: 160 }}
                  >
                    Back
                  </Button>
                </div>
              </>
            )}
          </Container>
        </div>
      </div>
    </Fade>,
    document.body
  );
};

export default GDPRPopup;